<template>
  <div class="group-question-selector" :class="{ 'tree-mode': treeMode }">
    <TableList
      ref="tableList"
      row-key="_id"
      no-operation
      :show-create-btn="false"
      :default-expand-all="false"
      :list-data="searchResult"
      :table-columns="tableColumns"
      :tree-props="{children: 'searchedChildren', hasChildren: 'hasChildren'}"
    >
      <template #filter-group>
        <el-select
          v-model="search.groupId"
          filterable
          clearable
          class="group-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.groupFilter`)"
        >
          <el-option
            v-for="(item, index) in groupList"
            :key="index"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-if="!treeMode && questionTypeList.length > 1"
          v-model="search.questionType"
          filterable
          clearable
          class="question-type-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.questionTypeFilter`)"
        >
          <el-option
            v-for="(item, index) in questionTypeList"
            :key="index"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-input
          v-model="search.keywords"
          clearable
          class="keywords-filter"
          :placeholder="$t(`${i18nPrefix}.placeholder.groupQuestionKeywordsFilter`)"
        />
      </template>
      <template #headerCheckbox>
        <div
          class="selection-checkbox-wrapper"
          @click="handleDisabledRowClick(null, null, true)"
        >
          <el-checkbox
            :value="isCheckedRow(null, null, true)"
            :indeterminate="isIndeterminateRow(null, null, true)"
            :disabled="treeMode && !selectableQuestionList.length"
            @change="handleRowCheckedChange(null, null, true)"
          />
        </div>
      </template>
      <template #bodyCheckbox="{ row, data }">
        <div
          v-if="!isTreeChildQuestion(row, data)"
          class="selection-checkbox-wrapper"
          @click="handleDisabledRowClick(row, data)"
        >
          <el-checkbox
            :value="isCheckedRow(row, data)"
            :indeterminate="isIndeterminateRow(row, data)"
            :disabled="isQuestionSelectionDisabled(row, data)"
            :title="getSelectionDisabledReason(row, data)"
            @change="handleRowCheckedChange(row, data)"
          />
        </div>
        <span v-else class="child-selection-placeholder" aria-hidden="true"></span>
      </template>
      <template #questionTitle="{row, formattedData}">
        <template v-if="formattedData">
          <div
            v-if="isTreeChildQuestion(row) && getTreeParentOptionLabel(row)"
            class="question-tree-parent-option"
          >{{ getTreeParentOptionLabel(row) }}</div>
          <div class="question-title-wrapper">
            <span
              class="title"
              :title="formattedData"
            >{{ formattedData }}</span>
            <el-button
              v-if="HAS_OPTIONS_QUESTION_TYPES.includes(row.type)"
              type="text"
              class="expand-btn"
              @click="handlerExpandQuestion($event, row)"
            >{{ $t(`${i18nPrefix}.btn.questionOptions.${questionExpand[row.id] ? 'collapse' : 'expand'}`) }}</el-button>
          </div>
          <transition
            v-if="HAS_OPTIONS_QUESTION_TYPES.includes(row.type)"
            name="flip"
            @before-enter="handleTransitionBeforeEnter"
            @before-leave="handleTransitionBeforeLeave"
          >
            <ul v-show="questionExpand[row.id]" class="question-options">
              <li
                v-for="(item, index) in row.options"
                class="option-item"
                :key="index"
                :class="item.is_knockout && 'knockout'"
              >
                {{ item.description }}
              </li>
            </ul>
          </transition>
        </template>
      </template>
    </TableList>
  </div>
</template>

<script>
import _ from 'lodash'
import TableList from '@/components/tableList/common.vue'
import { DATA_TYPE, QUESTION_TYPE, HAS_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { i18nPrefix, parseGroup } from './common'
import {
  decorateQuestionTreeRows,
  getOptionChildKey,
  getQuestionKey,
  inspectQuestionTree
} from '@/utils/openQuestionTree'

export default {
  components: {
    TableList
  },
  model: {
    prop: 'selected',
    event: 'change'
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    selected: {
      type: Array,
      required: true
    },
    disabledQuestionType: {
      type: Array,
      default: () => []
    },
    treeMode: {
      type: Boolean,
      default: false
    },
    hideUnavailableTrees: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix,
      QUESTION_TYPE,
      HAS_OPTIONS_QUESTION_TYPES,
      groupList: [],
      selectedQuestion: {},
      questionExpand: {},
      groupSelection: {},
      search: {
        groupId: null,
        questionType: null,
        keywords: null
      }
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    selectedWatcher () {
      return this.selected
    },
    searchResult () {
      const result = _.filter(this.groupList, group => {
        if (this.treeMode) {
          const matchedRootKeys = _.chain(group.children)
            .filter(question => {
              if (this.search.questionType && question.type !== this.search.questionType) {
                return false
              }
              if (this.search.keywords && !question.title.includes(this.search.keywords)) {
                return false
              }
              return true
            })
            .map('_treeRootKey')
            .uniq()
            .value()
          group.searchedChildren = _.filter(group.children, question => matchedRootKeys.includes(question._treeRootKey))
        } else {
          group.searchedChildren = _.filter(group.children, 'isShow')
        }
        return group.isShow
      })
      return result
    },
    questionList () {
      return _.flatMap(this.groupList, 'children')
    },
    selectableQuestionList () {
      if (!this.treeMode) {
        return this.questionList
      }
      return _.filter(this.questionList, question => {
        return question._treeDepth === 0 && !question._treeDisabled
      })
    },
    questionTypeList () {
      return _
        .chain(QUESTION_TYPE)
        .map((value, key) => {
          return { label: this.$t(`${this.i18nPrefix}.questionType.${key}`), value }
        })
        .filter(({ value }) => !this.disabledQuestionType.includes(value))
        .value()
    },
    tableRef () {
      return this.$refs.tableList.elTable
    },
    tableColumns () {
      const vm = this
      const columns = [
        {
          columnType: 'selection',
          width: this.treeMode ? 48 : 25,
          dataKey: '_id',
          slotName: 'bodyCheckbox',
          headerSlotName: 'headerCheckbox'
        },
        {
          width: this.treeMode ? 176 : 80,
          label: this.$t(`${this.i18nPrefix}.label.groupName`),
          cellClass: 'group-name',
          dataKey: 'name',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.GROUP) {
              return data
            }
          }
        },
        {
          width: this.treeMode ? 110 : 50,
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          dataKey: 'type',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              const type = _.findKey(QUESTION_TYPE, item => item === data)
              return vm.$t(`${vm.i18nPrefix}.questionType.${type}`)
            }
          }
        },
        {
          width: this.treeMode ? 329 : 150,
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          dataKey: 'title',
          cellClass: 'question-title',
          slotName: 'questionTitle',
          showOverflowTooltip: false,
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              return data
            }
          }
        }
      ]
      return columns
    }
  },
  created () {

  },
  mounted () {
    const tableRef = this.tableRef
    const selectionCol = _.find(tableRef.$children, { type: 'selection' })
    const colConfig = selectionCol.columnConfig
    const originRenderHeader = colConfig.renderHeader
    colConfig.renderHeader = (h, scope) => {
      const renderHeader = selectionCol.$scopedSlots.header
      return renderHeader ? renderHeader(scope) : originRenderHeader(h, scope)
    }
  },
  methods: {
    refreshGroupList () {
      const search = this.search
      const questionExpand = {}
      const groupList = this.data.map(parseGroup).filter(group => group.children?.length > 0)
      groupList.forEach(group => {
        if (this.treeMode) {
          group.children = decorateQuestionTreeRows(group.children)
          const inspection = inspectQuestionTree(group.children)
          const invalidKeys = new Set([
            ...inspection.cycleKeys,
            ...inspection.multipleParentKeys,
            ...inspection.overDepthKeys,
            ..._.map(inspection.danglingReferences, 'parentKey')
          ])
          const adjacency = {}
          _.forEach(group.children, question => {
            const questionKey = getQuestionKey(question)
            adjacency[questionKey] = adjacency[questionKey] || new Set()
            _.forEach(question.options || [], option => {
              const childKey = getOptionChildKey(option)
              if (!childKey || !_.some(group.children, item => getQuestionKey(item) === childKey)) {
                return
              }
              adjacency[childKey] = adjacency[childKey] || new Set()
              adjacency[questionKey].add(childKey)
              adjacency[childKey].add(questionKey)
            })
          })
          const invalidQueue = Array.from(invalidKeys)
          while (invalidQueue.length) {
            const invalidKey = invalidQueue.shift()
            _.forEach(Array.from(adjacency[invalidKey] || []), relatedKey => {
              if (!invalidKeys.has(relatedKey)) {
                invalidKeys.add(relatedKey)
                invalidQueue.push(relatedKey)
              }
            })
          }
          _.forEach(group.children, question => {
            question._treeDisabled = this.disabledQuestionType.includes(question.type) ||
              invalidKeys.has(getQuestionKey(question))
            question._treeDisabledReason = ''
            if (invalidKeys.has(getQuestionKey(question))) {
              question._treeDisabledReason = 'invalidTemplateTree'
            } else if (this.disabledQuestionType.includes(question.type)) {
              question._treeDisabledReason = 'unsupportedTemplateType'
            }
          })
          _.forEach(group.children, question => {
            if (question._treeDisabled && question._treeRootKey) {
              const rootQuestion = _.find(group.children, item => item._treeRootKey === question._treeRootKey && item._treeDepth === 0)
              if (rootQuestion) {
                rootQuestion._treeDisabled = true
                rootQuestion._treeDisabledReason = question._treeDisabledReason
              }
            }
          })
          if (this.hideUnavailableTrees) {
            const availableRootKeys = new Set(_.chain(group.children)
              .filter(question => question._treeDepth === 0 && !question._treeDisabled)
              .map('_treeRootKey')
              .value())
            group.children = _.filter(group.children, question => availableRootKeys.has(question._treeRootKey))
          }
        }
        Object.defineProperty(group, 'isShow', {
          configurable: true,
          enumerable: true,
          get () {
            let show = this.searchedChildren.length > 0
            if (show && search.groupId) {
              show = this.id === search.groupId
            }
            return show
          }
        })
        const children = []
        _.forEach(group.children, question => {
          const questionType = question.type
          if (!this.treeMode && this.disabledQuestionType.includes(questionType)) {
            return
          }
          questionExpand[question.id] = false
          Object.defineProperty(question, 'isShow', {
            configurable: true,
            enumerable: true,
            get () {
              let show = true
              if (search.questionType) {
                show = this.type === search.questionType
              }
              if (show && search.keywords) {
                show = this.title.includes(search.keywords)
              }
              return show
            }
          })
          children.push(question)
        })
        group.children = children
        group.searchedChildren = children
      })
      this.questionExpand = questionExpand
      this.groupList = this.hideUnavailableTrees
        ? groupList.filter(group => group.children.length > 0)
        : groupList
    },
    refreshSelected () {
      const selectedQuestion = {}
      _.forEach(this.selected, question => {
        selectedQuestion[question.id] = question
      })
      _.forEach(this.selectableQuestionList, question => {
        if (!selectedQuestion[question.id]) {
          selectedQuestion[question.id] = null
        }
      })
      this.selectedQuestion = selectedQuestion
    },
    isQuestionRow (row, data) {
      return Boolean(row._parent)
    },
    isTreeChildQuestion (row, data) {
      return this.treeMode && this.isQuestionRow(row, data) && row._treeDepth > 0
    },
    getTreeParentOptionLabel (row) {
      const parentQuestion = _.find(row._parent.children, question => getQuestionKey(question) === row._treeParentKey)
      const parentOption = _.find(parentQuestion?.options || [], option => getOptionChildKey(option) === getQuestionKey(row))
      return parentOption?.description || ''
    },
    isQuestionSelectionDisabled (row, data) {
      if (!this.treeMode) return false
      return this.isQuestionRow(row, data) ? (row._treeDepth > 0 || row._treeDisabled) : !this.getSelectableGroupQuestions(row).length
    },
    getSelectableGroupQuestions (group) {
      return this.treeMode ? _.filter(group.children, question => question._treeDepth === 0 && !question._treeDisabled) : group.children
    },
    getSelectionDisabledReason (row, data) {
      if (!this.isQuestionSelectionDisabled(row, data)) return ''
      const reason = this.isQuestionRow(row, data)
        ? (row._treeDepth > 0 ? 'selectChildWithRoot' : row._treeDisabledReason)
        : 'noSelectableTemplate'
      return reason ? this.$t(`${this.i18nPrefix}.message.${reason}`) : ''
    },
    hasUnsupportedTemplateType (row, data, isHeader) {
      if (!this.treeMode || (this.isQuestionRow(row, data) && row._treeDepth > 0)) return false
      let targetQuestions = null
      if (isHeader) {
        targetQuestions = this.questionList
      } else if (this.isQuestionRow(row, data)) {
        targetQuestions = _.filter(row._parent.children, question => question._treeRootKey === row._treeRootKey)
      } else {
        targetQuestions = row.children
      }
      return _.some(targetQuestions, question => question._treeDisabledReason === 'unsupportedTemplateType')
    },
    warnUnsupportedTemplateType (row, data, isHeader) {
      if (this.hasUnsupportedTemplateType(row, data, isHeader)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.unsupportedTemplateType`))
      }
    },
    handleDisabledRowClick (row, data, isHeader) {
      const isDisabled = isHeader
        ? this.treeMode && !this.selectableQuestionList.length
        : this.isQuestionSelectionDisabled(row, data)
      if (!isDisabled || this.isCheckedRow(row, data, isHeader)) return
      this.warnUnsupportedTemplateType(row, data, isHeader)
    },
    isCheckedRow (row, data, isHeader) {
      if (isHeader) {
        return this.selected.length > 0 && this.selected.length === this.selectableQuestionList.length
      } else if (this.isQuestionRow(row, data)) {
        return Boolean(this.selectedQuestion[row.id])
      } else {
        const groupQuestionList = this.getSelectableGroupQuestions(row)
        if (groupQuestionList.length > 0) {
          const checked = _.filter(groupQuestionList, question => this.selectedQuestion[question.id])
          return checked.length === groupQuestionList.length
        } else {
          return this.treeMode ? false : this.isCheckedRow(null, null, true)
        }
      }
    },
    isIndeterminateRow (row, data, isHeader) {
      if (isHeader) {
        return this.selected.length > 0 && this.selected.length < this.selectableQuestionList.length
      } else if (this.isQuestionRow(row, data)) {
        return false
      } else {
        const groupQuestionList = this.getSelectableGroupQuestions(row)
        const checked = _.filter(groupQuestionList, question => this.selectedQuestion[question.id])
        return checked.length > 0 && checked.length < groupQuestionList.length
      }
    },
    handleRowCheckedChange (row, data, isHeader) {
      if (this.treeMode && (isHeader ? !this.selectableQuestionList.length : this.isQuestionSelectionDisabled(row, data))) return
      const isChecked = this.isCheckedRow(row, data, isHeader)
      if (!isChecked) {
        this.warnUnsupportedTemplateType(row, data, isHeader)
      }
      if (isHeader) {
        this.changeSelected(!isChecked)
      } else if (this.isQuestionRow(row, data)) {
        if (this.isQuestionSelectionDisabled(row, data)) {
          return
        }
        this.changeSelected(!isChecked, row)
      } else {
        this.changeSelected(!isChecked, null, row)
      }
    },
    changeSelected (isAdd, question, group) {
      let selected = null
      if (!question && !group) {
        if (isAdd) {
          selected = this.selectableQuestionList
        } else {
          selected = []
        }
      } else {
        if (question) {
          this.selectedQuestion[question.id] = isAdd ? question : null
        } else {
          const groupQuestionList = this.getSelectableGroupQuestions(group)
          _.forEach(groupQuestionList, question => {
            this.selectedQuestion[question.id] = isAdd ? question : null
          })
        }
        selected = _.filter(this.selectableQuestionList, question => this.selectedQuestion[question.id])
      }
      this.$emit('change', selected)
    },
    handlerExpandQuestion ($event, question) {
      const isExpand = this.questionExpand[question.id]
      this.questionExpand[question.id] = !isExpand
    },
    handleTransitionBeforeEnter (el) {
      el.style.display = null
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--height', `${rect.height}px`)
      el.style.display = 'none'
    },
    handleTransitionBeforeLeave (el) {
      const rect = el.getBoundingClientRect()
      el.style.setProperty('--height', `${rect.height}px`)
    },
    async expandAllRows () {
      await this.$nextTick()
      const hasSearch = Boolean(this.search.groupId || this.search.questionType || this.search.keywords)
      const tableRef = this.tableRef
      _.forEach(this.searchResult, group => {
        tableRef.toggleRowExpansion(group, hasSearch)
      })
    }
  },
  watch: {
    data: {
      handler () {
        this.refreshGroupList()
      },
      immediate: true
    },
    selectedWatcher: {
      handler () {
        this.refreshSelected()
      },
      immediate: true
    },
    search: {
      handler () {
        this.expandAllRows()
      },
      deep: true
    },
    questionTypeList: {
      handler () {
        if (this.questionTypeList === 1) {
          this.search.questionType = this.questionTypeList[0].value
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.group-question-selector {
  ::v-deep {
    .table-list-wrapper {
      .table-header {
        .filter-group {
          margin-right: 0;
        }
      }

      .table-container {
        td {
          padding: 10px 0;
        }

        td.group-name {
          .cell {
            text-align: left;
          }
        }

        td.question-title {
          .cell {
            padding: 0 10px;
            text-align: left;
          }
        }
      }
    }
  }

  .question-title-wrapper {
    display: flex;
    line-height: normal;

    .title {
      flex: 1;
      text-overflow: ellipsis;
      white-space: pre;
      overflow: hidden;
    }

    .expand-btn {
      margin: 0 0 auto auto;
      padding: 0;
      line-height: inherit;
    }
  }

  .question-tree-parent-option {
    color: #909399;
    font-size: 12px;
    display: inline-block;
    margin-bottom: 4px;
    padding: 3px 8px;
    border-radius: 2px;
    line-height: 18px;
    background: #f5f7fa;
    word-break: break-word;
  }

  .question-options {
    padding-left: 18px;
    color: #999;
    list-style-type: disc;
    overflow: hidden;
    transition: height .3s;

    &.flip-enter-active {
      height: 0;
    }

    &.flip-leave-active {
      height: var(--height, auto);
    }

    &.flip-enter-to {
      height: var(--height, auto);
    }

    &.flip-leave-to {
      height: 0;
    }
  }

  &.tree-mode {
    .question-title-wrapper {
      align-items: flex-start;

      .title {
        min-width: 0;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
    }

    .question-options {
      margin: 8px 0 0;

      .option-item {
        padding: 2px 0;
        overflow-wrap: anywhere;
        word-break: break-word;

        &.knockout {
          color: #f56c6c;
        }
      }

    }

    ::v-deep {
      .table-list-wrapper {
        .table-header {
          height: auto;
          margin: 0 0 20px;

          .filter-group {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            gap: 10px;

            > * {
              width: 100%;
              margin-right: 0;
            }
          }
        }

        .table-container {
          .el-table {
            color: #606266;
          }

          th {
            padding: 11px 0;
            color: #303133;
            background: #f5f7fa;
          }

          td {
            padding: 12px 0;
            vertical-align: top;
          }

          .el-table-column--selection .cell {
            padding-right: 12px;
            padding-left: 12px;
          }

          td.group-name .cell {
            overflow-wrap: anywhere;
            word-break: break-word;
          }
        }
      }
    }
  }
}

@media (max-width: 760px) {
  .group-question-selector.tree-mode {
    ::v-deep .table-list-wrapper .table-header .filter-group {
      grid-template-columns: minmax(0, 1fr);
    }
  }
}

.el-select-dropdown__item {
  min-width: 100%;
  max-width: 40vw;
  overflow: hidden;
  word-break: keep-all;
  text-overflow: ellipsis;
}
</style>
